/**
 * @file: ListEmptyState.tsx
 *
 * Generic centred empty-list placeholder: an icon (custom or the default
 * bell) above a title + optional subtitle. Shared by the inbox feed and
 * chat thread lists; pass a custom `icon` to override the default glyph.
 */

import { Typography } from '@euler/app/components/typography';
import { Icon } from '@euler/components/Icomoon';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FC, ReactNode, memo } from 'react';
import { View } from 'react-native';

export const ListEmptyState: FC<{
  title: string;
  subtitle?: string;
  icon?: ReactNode;
}> = memo(({ title, subtitle, icon }) => {
  const { rpx } = useResponsiveContext();
  return (
    <View
      css={`
        flex: 1;
        align-items: center;
        justify-content: center;
        padding: ${rpx(40)}px;
      `}
    >
      {icon ? (
        <View
          css={`
            margin-bottom: ${rpx(16)}px;
          `}
        >
          {icon}
        </View>
      ) : (
        <View
          css={`
            width: ${rpx(72)}px;
            height: ${rpx(72)}px;
            border-radius: ${rpx(36)}px;
            background-color: #f3f4f6;
            align-items: center;
            justify-content: center;
            margin-bottom: ${rpx(16)}px;
          `}
        >
          <Icon name="notifications-outline" size={rpx(28)} color="#9ca3af" />
        </View>
      )}
      <Typography.Text
        size={rpx(16)}
        color="#374151"
        style={{ fontFamily: FontFamily.NotoSans.Medium }}
      >
        {title}
      </Typography.Text>
      {subtitle ? (
        <Typography.Text mt={rpx(6)} size={rpx(12)} color="#9ca3af">
          {subtitle}
        </Typography.Text>
      ) : null}
    </View>
  );
});
